import React from 'react';
import { useGetMeteocodeByCodeStringQuery } from 'Services/api/meteoCodesApi';
import { useLazyGetLastDataValuesQuery } from 'Services/api/reportsApi';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import { useSelector } from 'react-redux';
import { timeZoneSelector } from 'Features/App/appSlice';
import { UTCtoTimeZone } from 'Utils/UTCConverter';
import { WEBSOCKETS } from 'Services/websocket';
import CardWidget from './CardWidget/Card.Widget';

export function useWidgetData({ request, params = { hasWebsocket: false, hasTimeout: false } }) {
  const { dataTS } = WEBSOCKETS;
  const selectedTimeZone = useSelector(timeZoneSelector, (a, b) => a.selectedUserTimeZone === b.selectedUserTimeZone);

  const {
    hasWebsocket,
  } = params;
  const [trigger, { data = [] }] = useLazyGetLastDataValuesQuery();
  const [values, setValues] = React.useState([]);

  const { subscriptionPaths, meteocodes } = React.useMemo(() => {
    let paths = [];
    const tmpMeteocodes = request.requestItems
      .map((column) => ({ code: column.meteoCode, stationEntityId: column.stationEntityId }));
    if (request && hasWebsocket) {
      paths = [...new Set(request.requestItems.map((column) => `${process.env.REACT_APP_WS_TS_PATH}/${column.sourceType}/${column.sourceId}`))];
    }
    return { subscriptionPaths: paths, meteocodes: tmpMeteocodes };
  }, [hasWebsocket, request]);

  const fetchData = React.useCallback(async () => {
    if (request) {
      const { startDate, endDate, includeOffGridData, ...rest } = request;
      await trigger({ ...rest });
    }
  }, [request, trigger]);

  const countValues = request?.requestItems.length || 0;

  React.useEffect(() => {
    if (countValues > 0 && data.length > 0) {
      const newVals = [];
      Array.from({ length: countValues }).forEach((_, index) => {
        const responseValue = data[index].response;
        const value = {
          t: '',
          v: '-',
        };
        if (responseValue) {
          value.t = UTCtoTimeZone(responseValue.dateTime, selectedTimeZone.selectedUserTimeZone, 'yyyy.MM.dd HH:mm:ss');
          value.v = responseValue.valueRound || responseValue.value;
        }
        newVals.push(value);
      });
      setValues(newVals);
    }
  }, [countValues, data, selectedTimeZone.selectedUserTimeZone]);

  React.useEffect(() => {
    if (dataTS.isConnected && subscriptionPaths.length > 0) {
      dataTS.subscribe(subscriptionPaths, (msg) => {
        const index = request.requestItems
          .findIndex((column) => column.meteocode === msg.meteoCode.codeString && column.stationEntityId === msg.stationEntityId);

        if (index !== -1) {
          setValues((cur) => {
            const tmp = JSON.parse(JSON.stringify(cur));
            const currentTime = tmp[index] ? new Date(tmp[index].t).getTime() : null;
            const msgTime = new Date(UTCtoTimeZone(msg.dateTime, selectedTimeZone.selectedUserTimeZone, 'yyyy.MM.dd HH:mm:ss')).getTime();

            if (!currentTime || msgTime >= currentTime) {
              tmp[index] = { t: UTCtoTimeZone(msg.dateTime, selectedTimeZone.selectedUserTimeZone, 'yyyy.MM.dd HH:mm:ss'), v: msg.valueRound || msg.value };
            }

            return tmp;
          });
        }
      });
    }
  }, [dataTS, dataTS.isConnected, meteocodes, request.requestItems, selectedTimeZone.selectedUserTimeZone, subscriptionPaths]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  React.useLayoutEffect(() => {
    dataTS.unsubscribe(subscriptionPaths[0]);
  }, [dataTS, selectedTimeZone.selectedUserTimeZone, subscriptionPaths]);

  return { values, meteocodes };
}

export function WidgetWrapper({ object, Widget, hasWebsocket = false, hasTimeout = false, isCommon = false }) {
  const params = {
    hasWebsocket,
    hasTimeout,
  };
  const { values, meteocodes } = useWidgetData({ request: object, params });

  const {
    data: meteocode, isFetching,
  } = useGetMeteocodeByCodeStringQuery(meteocodes[0].code, { skip: !isCommon });

  const additionalInfo = React.useMemo(() => {
    let info = {};
    if (isCommon && meteocode) {
      info = {
        name: meteocode.name,
        unit: meteocode?.code?.unit?.unit || '',
      };
    }
    return info;
  }, [isCommon, meteocode]);

  return (
    <CardWidget
      tooltip={<CardTooltipContent meteocodes={meteocodes} dates={values.map((v) => v?.t || null)} />}
    >
      {isFetching
        ? <SuspenseSpinner size={60} />
        : (
          <Widget
            values={values.map((v) => v?.v || null)}
            additionalInfo={additionalInfo}
          />
        )}
    </CardWidget>
  );
}

function CardTooltipContent({ meteocodes = [], dates = [] }) {
  return (
    <div className="card-tooltip-wrapper">
      {React.Children.toArray(meteocodes.map((meteocode, index) => (
        <MeteoCodeInfo meteocode={meteocode} date={dates[index]} />
      )))}
    </div>
  );
}

function MeteoCodeInfo({ meteocode, date }) {
  const selectedTimeZone = useSelector(timeZoneSelector, (a, b) => a.selectedUserTimeZone === b.selectedUserTimeZone);
  const { data: meteoCode, isFetching } = useGetMeteocodeByCodeStringQuery(meteocode.code);

  if (isFetching) return <SuspenseSpinner size={40} color="white" />;

  return (
    <div>
      <p>
        <b>Дата: </b>
        {`${date || '-'} (UTC${selectedTimeZone.selectedUserTimeZone})`}
      </p>
      <p>
        <b>Метеокод: </b>
        {meteocode.code}
      </p>
      <p>
        <b>Название: </b>
        {meteoCode?.name}
      </p>
    </div>
  );
}
